import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Zap,
  CheckCircle2,
  FileText,
  ClipboardPlus,
  Smile,
  ScanLine,
  Braces,
  Workflow,
  Sparkles,
  PlayCircle,
  MousePointerClick,
  ListChecks,
  Contrast,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { PageTransition } from "@/components/Reveal";
import { CTABand } from "@/components/CTA";
import { trackButton } from "@/lib/analytics";
import { Seo } from "@/components/Seo";

const stats = [
  { value: "99.2%", label: "Field-level extraction accuracy" },
  { value: "10x", label: "Faster than manual keying" },
  { value: "70%", label: "Lower cost per claim" },
  { value: "<15s", label: "Average document turnaround" },
];

const docTypes = [
  {
    icon: FileText,
    title: "HCFA / CMS-1500",
    desc: "Professional claims from providers, captured field-by-field with box-level precision.",
    span: "lg:col-span-2",
  },
  {
    icon: ClipboardPlus,
    title: "UB-04 / CMS-1450",
    desc: "Institutional claims with revenue codes, value codes, and occurrence spans.",
    span: "",
  },
  {
    icon: Smile,
    title: "Dental (ADA)",
    desc: "ADA dental claim forms with procedure codes and tooth-level detail.",
    span: "",
  },
  {
    icon: Sparkles,
    title: "EOBs, superbills & more",
    desc: "Beyond claims — remittances, prior-auth packets, and clinical attachments.",
    span: "lg:col-span-2",
  },
];

const steps = [
  { icon: ScanLine, title: "Ingest", desc: "Upload scans, faxes, PDFs, or connect a mailbox. Any quality, any layout." },
  { icon: Braces, title: "Extract", desc: "AI reads every field, table, and handwritten note into structured data." },
  { icon: CheckCircle2, title: "Validate", desc: "Business rules, payer edits, and confidence scoring flag issues before they cost you." },
  { icon: Workflow, title: "Integrate", desc: "Push clean data straight into your EHR, clearinghouse, or RCM platform via API." },
];

const Home = () => {
  const navigate = useNavigate();

  const scrollToDemo = () => {
    navigate("/demo/hcfa-cms-1500");
  };

  return (
    <PageTransition>
      <Seo
        title="Hewto.ai | Agentic Document Extraction for Healthcare Claim Forms"
        description="Hewto.ai is agentic document extraction for healthcare — vision-language models fine-tuned on HCFA / CMS-1500, UB-04, and ADA J430D claim forms turn scans and faxes into clean, validated, 837-ready data. Agentic document extraction for claim forms, built for US healthcare."
        keywords="agentic document extraction for healthcare, agentic document extraction for claim forms, agentic document extraction, agentic ocr healthcare, vision language model claim form extraction, healthcare document processing, medical claims ocr, hcfa ocr, ub-04 ocr, dental claim ocr, intelligent document processing healthcare, cms 1500 agentic extraction, ada j430d agentic extraction"
        canonical="https://www.hewto.ai/"
        image="https://www.hewto.ai/cms1500-form.png"
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-grid opacity-60" aria-hidden="true" />
        <div
          className="absolute -right-40 -top-40 h-[500px] w-[500px] rounded-full bg-brand-50 blur-3xl"
          aria-hidden="true"
        />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:py-28">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-100 bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-600">
                <Sparkles className="h-4 w-4" /> Agentic Document Extraction for Healthcare
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 text-4xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
                Medical precision meets <span className="text-brand">AI velocity</span>
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
                Hewto.ai turns messy HCFA, UB-04, and dental claim forms into clean, validated,
                integration-ready data — in seconds. Built for the accuracy and compliance US
                healthcare demands.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="mt-8 flex flex-wrap gap-4">
                <Button
                  data-testid="hero-book-demo-btn"
                  onClick={() => {
                    trackButton("Book Demo", { source: "hero" });
                    navigate("/contact-sales");
                  }}
                  className="group rounded-full bg-brand px-6 py-6 text-base font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-brand-600"
                >
                  Book a Demo
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  data-testid="hero-products-btn"
                  onClick={() => navigate("/cms-1500-ocr")}
                  variant="outline"
                  className="rounded-full border-slate-200 bg-white px-6 py-6 text-base font-semibold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-slate-50"
                >
                  Explore the Platform
                </Button>
                <Button
                  data-testid="hero-watch-demo-btn"
                  onClick={scrollToDemo}
                  variant="ghost"
                  className="group rounded-full px-6 py-6 text-base font-semibold text-brand transition-colors hover:bg-brand-50 hover:text-brand-600"
                >
                  <PlayCircle className="mr-2 h-5 w-5" />
                  Watch Demo
                </Button>
              </div>
            </Reveal>
            <Reveal delay={0.32}>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-500">
                <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand" /> 99.2% extraction accuracy</span>
                <span className="inline-flex items-center gap-2"><Zap className="h-4 w-4 text-brand" /> Sub-15s processing</span>
              </div>
            </Reveal>
          </div>

          {/* Visual */}
          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="relative">
                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl shadow-brand/10">
                  <img
                    src="/cms1500-form.png"
                    alt="HCFA / CMS-1500 health insurance claim form"
                    className="h-72 w-full object-cover object-top sm:h-96"
                  />
                </div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  className="absolute -bottom-6 -left-6 w-56 rounded-2xl border border-slate-100 bg-white p-4 shadow-xl"
                >
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-brand-50 text-brand">
                      <FileText className="h-3.5 w-3.5" />
                    </span>
                    CMS-1500 · Box 24
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400">CPT</span>
                      <span className="font-mono font-semibold text-slate-900">99213</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400">Charge</span>
                      <span className="font-mono font-semibold text-slate-900">$185.00</span>
                    </div>
                    <div className="flex items-center gap-1.5 pt-1 text-xs font-medium text-emerald-600">
                      <CheckCircle2 className="h-3.5 w-3.5" /> 99.4% confidence
                    </div>
                  </div>
                </motion.div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* USP STRIP */}
      <section data-testid="usp-strip" className="relative overflow-hidden bg-slate-950 py-20 text-slate-100">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 15% 20%, rgba(4, 135, 216, 0.35) 0, transparent 45%), radial-gradient(circle at 85% 80%, rgba(4, 135, 216, 0.25) 0, transparent 50%)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-200/30 bg-brand-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-200">
                <Sparkles className="h-3.5 w-3.5" /> Why Hewto.ai wins
              </span>
              <h2 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                OCR that sees forms the way humans do
              </h2>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              {
                icon: MousePointerClick,
                title: "Fine-tuning at the click of a button",
                desc: "Upload a handful of labeled samples and Hewto.ai fine-tunes a purpose-built vision-language model on your exact forms. No ML team, no pipelines, no code.",
              },
              {
                icon: ListChecks,
                title: "Reads checkboxes, crosses & ticks like a human",
                desc: "Checked, crossed, or ticked — our models understand selection marks the way the human mind does, including partially marked and ambiguous boxes.",
              },
              {
                icon: Contrast,
                title: "Signal separated from noise",
                desc: "Watermark text vs value text, label text vs value text, background vs foreground — Hewto.ai tells them apart so only real data lands in your systems.",
              },
            ].map((u, i) => (
              <Reveal key={u.title} delay={i * 0.08}>
                <div
                  data-testid={`usp-card-${i}`}
                  className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                    <u.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-lg font-semibold text-white">{u.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-300">{u.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF / STATS */}
      <section className="border-y border-slate-100 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8">
          <Reveal>
            <p className="text-center text-sm font-semibold uppercase tracking-widest text-slate-400">
              Trusted by payers, providers, and RCM teams processing millions of claims
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-8 lg:grid-cols-4">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div className="text-center">
                  <div className="text-4xl font-bold tracking-tight text-brand sm:text-5xl">{s.value}</div>
                  <div className="mt-2 text-sm text-slate-600">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM / SOLUTION */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg">
              <img
                src="/ub04-form.png"
                alt="UB-04 / CMS-1450 institutional claim form"
                className="h-80 w-full object-cover object-top"
                loading="lazy"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
                The paper problem
              </span>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Manual claim entry is slow, costly, and error-prone
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-slate-600">
                Every faxed HCFA, scanned UB-04, and dental attachment gets keyed by hand — creating
                denials, rework, and days of delay. Hewto.ai replaces that with an AI pipeline that
                reads any layout and hands you clean, validated data.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Reads low-quality scans, faxes, and handwriting",
                  "Understands box-level structure of CMS forms",
                  "Confidence scores + validation catch errors upstream",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* DOCUMENT TYPES BENTO */}
      <section className="bg-slate-50 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
                Document coverage
              </span>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Built for the forms US healthcare runs on
              </h2>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {docTypes.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.08} className={d.span}>
                <div className="group h-full rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-brand/10">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                    <d.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 text-xl font-semibold text-slate-900">{d.title}</h3>
                  <p className="mt-2 leading-relaxed text-slate-600">{d.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              How it works
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              From document to data in four steps
            </h2>
          </div>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, i) => (
            <Reveal key={step.title} delay={i * 0.1}>
              <div className="relative rounded-2xl border border-slate-200 bg-white p-8">
                <span className="absolute right-6 top-6 text-5xl font-bold text-slate-100">
                  {i + 1}
                </span>
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand text-white">
                  <step.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABand />
    </PageTransition>
  );
};

export default Home;